import { useState } from 'react'

import {
  orchestrator,
  releasableStopIds,
  requiredStopChecks,
  useReleaseStops,
  type StopItem,
  type StopPhysicalCheck,
  type StopsList,
} from '@ctcells/api'
import { t, type MessageKey } from '@ctcells/i18n'

import { AddModal } from '../atoms/AddModal.tsx'
import { Button } from '../atoms/Button.tsx'
import { Checkbox } from '../atoms/Checkbox.tsx'
import { Field } from '../atoms/Field.tsx'
import { useSubmitValidation } from '../atoms/useSubmitValidation.ts'
import { TextInput } from '../atoms/TextInput.tsx'
import type { ActionGate } from '../screens/access.ts'
import { FormSection, ModalFooter } from '../screens/formSections.tsx'

import { releaseStopsAndSettle } from './stopsRelease.ts'

const listStops = async (): Promise<readonly StopItem[]> =>
  (
    await orchestrator.request<StopsList>({
      method: 'GET',
      path: '/safety/stops',
      retryOn401: true,
    })
  ).items

/**
 * 시작 창의 **[정지 풀기]** — 문 열림(`door_open`) 같은 «풀 수 있는 정지»를 그 자리에서 푼다(`POST /safety/stops/release` · `IFC-RO-110`).
 *
 * 실측(히나 2026-10-24 로컬) — 원심 `open_door` 로 연 문은 `GET /safety/stops` 에 `door_open:centrifuge-1` 로 서고, 사전 점검은
 * `SAFETY_SELFTEST_FAIL · safety.interlock` 로 막는다. 🔴 `POST /safety/clear` 는 200 이어도 이 항목을 안 푼다(안전 상태가 이미 `normal`).
 * 현장 확인 없이 풀면 422 `physical_check_required`(`context.checks: ['door_area']`), 확인을 실으면 200 `done` 이고 약 3초 뒤 목록 · 점검에서 빠진다.
 *
 * 🔴 **`ids` 는 지금 «풀 항목» 전부다** — 서버가 집합이 같은지 본다(골라 풀기 아님 · 다르면 409 `STOPS_CHANGED`). 그래서 창이 그 목록을
 *    **전부 보이고**, 열 때 «지금» 목록을 새로 읽는다.
 * 🔴 **현장 확인은 사람이 체크한다** — 풀 항목 종류가 요구하는 확인(`requiredStopChecks` · 서버 어휘 그대로)마다 체크 하나. 다 체크해야 보낸다.
 * 🔴 **못 풀면 사유를 보인다** — 항목별 `done=false` 면 그 항목의 서버 문장 · 코드를 적고 멈춘다. 작업을 다시 만들지 않는다.
 * 🔴 **작업을 끊지 않는다** — 이 조치는 정지만 푼다(eric 2026-10-24 「이슈만 정리해야지, 작업은 유지하고」).
 */
export function StopsReleaseFix({
  gate,
  onRun,
}: {
  /** 제어 권한 · 제어락 · 끊김 · 다른 조치가 도는 중 — 부르는 쪽이 짓는다 */
  gate: ActionGate
  onRun: (send: () => Promise<unknown>) => void
}) {
  const release = useReleaseStops()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<readonly StopItem[] | null>(null)
  const [readError, setReadError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const validation = useSubmitValidation((value: string) => ({
    reason: value.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = validation.field('reason')
  const [checked, setChecked] = useState<ReadonlySet<StopPhysicalCheck>>(new Set())

  const ids = items === null ? [] : releasableStopIds([...items])
  const checks = items === null ? [] : requiredStopChecks(items, ids)
  const humanFirst = (items ?? []).filter((item) => !item.releasable)

  /** 열 때마다 «지금» 목록을 읽는다 — 사람이 본 목록이 서버 집합이다 */
  const openDialog = () => {
    setOpen(true)
    setItems(null)
    setReadError(null)
    listStops().then(setItems, (error: unknown) => {
      setReadError(error instanceof Error ? error.message : String(error))
    })
  }
  const close = () => {
    setOpen(false)
    setReason('')
    validation.reset()
    setChecked(new Set())
  }

  const submitGate: ActionGate = gate.disabled
    ? gate
    : items === null
      ? { disabled: true, disabledReason: t('pfStops.loading') }
      : ids.length === 0
        ? { disabled: true, disabledReason: t('pfStops.none') }
        : checks.some((check) => !checked.has(check))
          ? { disabled: true, disabledReason: t('reason.physicalChecksRequired') }
          : { disabled: false }

  return (
    <>
      <Button onClick={openDialog} size="pill" {...gate}>
        {t('pfStops.action')}
      </Button>
      {open ? (
        <AddModal size="md" onClose={close} title={t('pfStops.action')}>
          <FormSection>
            {readError !== null ? (
              <p className="vissue" role="alert">
                {readError}
              </p>
            ) : items === null ? (
              <p className="small muted" role="status">
                {t('pfStops.loading')}
              </p>
            ) : (
              <>
                <p className="small">{t('pfStops.releasable')}</p>
                {ids.length === 0 ? (
                  <p className="small muted">{t('pfStops.none')}</p>
                ) : (
                  <ul className="lbwfix__list">
                    {items
                      .filter((item) => ids.includes(item.id))
                      .map((item) => (
                        /* 🔴 서버가 지은 문장 그대로 — 다시 짓지 않는다 */
                        <li key={item.id}>{item.said}</li>
                      ))}
                  </ul>
                )}
                {humanFirst.length === 0 ? null : (
                  <>
                    <p className="small">{t('stops.humanFirst')}</p>
                    <ul className="lbwfix__list">
                      {humanFirst.map((item) => (
                        <li key={item.id}>{item.said}</li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
            <Field {...reasonField.fieldProps} label={t('field.reason')} required>
              <TextInput
                {...reasonField.inputProps}
                onChange={(next) => {
                  setReason(next)
                  validation.change('reason', next)
                }}
                value={reason}
                wide
              />
            </Field>
            {checks.map((check) => (
              <Checkbox
                checked={checked.has(check)}
                key={check}
                label={t(`enum.resumeCheck.${check}` as MessageKey)}
                onChange={(on) => {
                  setChecked((all) => {
                    const next = new Set(all)
                    if (on) next.add(check)
                    else next.delete(check)
                    return next
                  })
                }}
              />
            ))}
          </FormSection>
          <ModalFooter>
            <Button onClick={close} size="pill">
              {t('action.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (submitGate.disabled) return
                if (
                  !validation.validate(
                    reason,
                    document.getElementById(reasonField.inputProps.id)?.parentElement ?? null,
                  )
                )
                  return
                const input = { ids, reason: reason.trim(), physicalChecks: checks }
                close()
                onRun(() =>
                  releaseStopsAndSettle(
                    {
                      listStops,
                      release: ({ ids: sent, reason: said, physicalChecks }) =>
                        release.mutateAsync({
                          ids: sent,
                          reason: said,
                          physicalChecked: false,
                          physicalChecks,
                        }),
                      sleep: (ms) =>
                        new Promise((resolve) => {
                          setTimeout(resolve, ms)
                        }),
                      now: () => Date.now(),
                    },
                    input,
                  ),
                )
              }}
              size="pill"
              variant="primary"
              {...submitGate}
            >
              {t('pfStops.action')}
            </Button>
          </ModalFooter>
        </AddModal>
      ) : null}
    </>
  )
}
